/**
 * useWebSpeechPII — hook de détection PII audio live, zéro coût.
 *
 * Utilise la Web Speech API (SpeechRecognition) du navigateur pour transcrire
 * localement le micro courant, puis détecte les PII via regex locales.
 * Chaque détection POST `{type, hash}` au serveur qui renvoie le niveau d'escalade.
 *
 * 🔒 Texte brut jamais envoyé au serveur ni loggé.
 *
 * @param {Object} opts
 * @param {string} opts.bookingId
 * @param {string} opts.token - JWT
 * @param {string} opts.apiUrl - REACT_APP_BACKEND_URL/api
 * @param {boolean} opts.active - activer/désactiver l'écoute
 * @param {(event: {rappel_level, type, message}) => void} opts.onDetection
 * @returns {{supported: boolean, listening: boolean, lastDetection: Object|null}}
 */
import { useEffect, useRef, useState } from 'react';
import { detectPII, isSpeechRecognitionSupported } from '../utils/piiDetector';

export default function useWebSpeechPII({ bookingId, token, apiUrl, active, onDetection }) {
  const [supported] = useState(() => isSpeechRecognitionSupported());
  const [listening, setListening] = useState(false);
  const [lastDetection, setLastDetection] = useState(null);
  const recognitionRef = useRef(null);
  const seenHashesRef = useRef(new Set()); // dédup local
  const inFlightRef = useRef(new Set());   // anti double-POST

  useEffect(() => {
    if (!supported || !active || !bookingId || !token) return;

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.lang = 'fr-FR';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    const handleResult = async (event) => {
      // On ne traite que les segments interim/final récents
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (!transcript || transcript.length < 8) continue;
        let detections;
        try {
          detections = await detectPII(transcript);
        } catch { continue; }
        for (const d of detections) {
          const key = `${d.type}:${d.hash}`;
          if (seenHashesRef.current.has(key) || inFlightRef.current.has(key)) continue;
          seenHashesRef.current.add(key);
          inFlightRef.current.add(key);
          // POST vers serveur — fire & forget, pas besoin d'attendre
          fetch(`${apiUrl}/marketplace/sessions/${bookingId}/pii-event`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ type: d.type, hash: d.hash }),
          })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              inFlightRef.current.delete(key);
              if (!data || data.duplicate) return;
              setLastDetection({ ...data, type: d.type });
              onDetection?.({ ...data, type: d.type });
            })
            .catch(() => { inFlightRef.current.delete(key); });
        }
      }
    };

    const handleError = (e) => {
      // "no-speech", "aborted" sont normaux
      if (e.error && !['no-speech', 'aborted', 'audio-capture'].includes(e.error)) {
        console.warn('[PII-SR] error:', e.error);
      }
    };

    const handleEnd = () => {
      // Auto-restart tant que actif (Chrome coupe après ~60s de silence)
      if (active && recognitionRef.current === recognition) {
        try { recognition.start(); } catch { /* already started */ }
      } else {
        setListening(false);
      }
    };

    recognition.onresult = handleResult;
    recognition.onerror = handleError;
    recognition.onend = handleEnd;
    recognition.onstart = () => setListening(true);

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch { /* ignore if already started */ }

    return () => {
      recognitionRef.current = null;
      try { recognition.stop(); } catch { /* ignore */ }
      setListening(false);
    };
  }, [supported, active, bookingId, token, apiUrl, onDetection]);

  return { supported, listening, lastDetection };
}
